import React from "react";

/**
 * 포토 레이아웃 상세정보 모달 컴포넌트
 * @param {Object} props - 컴포넌트 속성
 * @param {boolean} props.isVisible - 모달 표시 여부
 * @param {Object} props.photoLayout - 포토 레이아웃 정보
 * @param {Function} props.onClose - 모달 닫기 함수
 */
const PhotoLayoutDetailModal = ({
    isVisible,
    photoLayout,
    onClose,
}) => {
    if (!isVisible || !photoLayout) return null;

    // 방향 표시 헬퍼
    const getDirectionDisplay = (direction) => {
        switch (direction) {
            case 'horizontal':
                return '가로';
            case 'vertical':
                return '세로';
            default:
                return '-';
        }
    };

    // 날짜 포맷팅 헬퍼
    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '무제한';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}${month}${day}`;
    };

    return (
        <div className="modal-overlay active" id="layout-detail-modal">
            <div className="modal-container">
                <div className="modal-header">
                    <h3 className="modal-title">레이아웃 상세정보</h3>
                    <button className="modal-close" onClick={onClose}>
                        &times;
                    </button>
                </div>
                <div className="modal-body">
                    {/* 기본 정보 */}
                    <h4 className="section-title">기본 정보</h4>
                    <div className="detail-item-container">
                        <span className="detail-label">레이아웃명</span>
                        <span className="detail-content">{photoLayout.layoutName}</span>
                    </div>
                    
                    <div className="detail-item-container">
                        <span className="detail-label">작성자</span>
                        <span className="detail-content">{photoLayout.createdBy}</span>
                    </div>

                    {/* 레이아웃 설정 */}
                    <h4 className="section-title">레이아웃 설정</h4>
                    <div className="detail-item-container">
                        <span className="detail-label">레이아웃 타입</span>
                        <span className="detail-content">
                            {photoLayout.layoutType || photoLayout.layoutCount || '-'}
                        </span>
                    </div>
                    
                    <div className="detail-item-container">
                        <span className="detail-label">레이아웃 방향</span>
                        <span className="detail-content">
                            {getDirectionDisplay(photoLayout.layoutDirection || photoLayout.direction)}
                        </span>
                    </div>

                    {/* 사용 기간 */}
                    <h4 className="section-title">사용 기간</h4>
                    <div className="detail-item-container">
                        <span className="detail-label">기간 설정</span>
                        <span className="detail-content">
                            {formatDateForDisplay(photoLayout.startDatetime)} - {formatDateForDisplay(photoLayout.endDatetime)}
                        </span>
                    </div>
                    
                    {/* QR 코드 설정 */}
                    {(photoLayout.qrX !== undefined || photoLayout.qrY !== undefined || photoLayout.qrSize !== undefined) && (
                        <>
                            <h4 className="section-title">QR 코드 설정</h4>
                            <div className="detail-item-container">
                                <span className="detail-label">위치</span>
                                <span className="detail-content">
                                    X: {photoLayout.qrX || '-'}, Y: {photoLayout.qrY || '-'}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">크기</span>
                                <span className="detail-content">{photoLayout.qrSize || '-'}</span>
                            </div>
                        </>
                    )}
                    
                    {/* 로고 설정 */}
                    {(photoLayout.logoResourceId || photoLayout.logoW !== undefined || photoLayout.logoH !== undefined) && (
                        <>
                            <h4 className="section-title">로고 설정</h4>
                            {photoLayout.logoResourceId && (
                                <div className="detail-item-container">
                                    <span className="detail-label">로고 리소스 ID</span>
                                    <span className="detail-content">{photoLayout.logoResourceId}</span>
                                </div>
                            )}
                            <div className="detail-item-container">
                                <span className="detail-label">로고 크기</span>
                                <span className="detail-content">
                                    너비: {photoLayout.logoW || '-'}, 높이: {photoLayout.logoH || '-'}
                                </span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">로고 위치</span>
                                <span className="detail-content">
                                    X: {photoLayout.logoX || '-'}, Y: {photoLayout.logoY || '-'}
                                </span>
                            </div>
                        </>
                    )}
                    
                    {/* 레이아웃 크기 설정 */}
                    {(photoLayout.widthMm !== undefined || photoLayout.heightMm !== undefined) && (
                        <>
                            <h4 className="section-title">레이아웃 크기</h4>
                            <div className="detail-item-container">
                                <span className="detail-label">크기 (mm)</span>
                                <span className="detail-content">
                                    너비: {photoLayout.widthMm || '-'}mm, 높이: {photoLayout.heightMm || '-'}mm
                                </span>
                            </div>
                        </>
                    )}
                    
                    {/* 여백 설정 */}
                    {(photoLayout.marginX !== undefined || photoLayout.marginY !== undefined || photoLayout.gutterX !== undefined) && (
                        <>
                            <h4 className="section-title">여백 설정</h4>
                            <div className="detail-item-container">
                                <span className="detail-label">여백</span>
                                <span className="detail-content">
                                    X: {photoLayout.marginX || '-'}, Y: {photoLayout.marginY || '-'}
                                </span>
                            </div>
                            {photoLayout.gutterX !== undefined && (
                                <div className="detail-item-container">
                                    <span className="detail-label">X 거터</span>
                                    <span className="detail-content">{photoLayout.gutterX || '-'}</span>
                                </div>
                            )}
                        </>
                    )}

                    {/* 배포 여부 */}
                    <div className="detail-item-container">
                        <span className="detail-label">배포 여부</span>
                        <span className="detail-content">
                            {photoLayout.isDeployed ? 'Y' : 'N'}
                        </span>
                    </div>

                    {/* 추가 정보 */}
                    <h4 className="section-title">추가 정보</h4>
                    <div className="detail-item-container">
                        <span className="detail-label">생성일시</span>
                        <span className="detail-content">
                            {photoLayout.createdAt 
                                ? new Date(photoLayout.createdAt).toLocaleString('ko-KR') 
                                : '-'}
                        </span>
                    </div>
                    
                    {photoLayout.updatedAt && (
                        <div className="detail-item-container">
                            <span className="detail-label">수정일시</span>
                            <span className="detail-content">
                                {new Date(photoLayout.updatedAt).toLocaleString('ko-KR')}
                            </span>
                        </div>
                    )}
                    
                    {photoLayout.updatedBy && (
                        <div className="detail-item-container">
                            <span className="detail-label">수정자</span>
                            <span className="detail-content">{photoLayout.updatedBy}</span>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        className="modal-btn modal-btn-primary" 
                        onClick={onClose}
                    >
                        확인
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PhotoLayoutDetailModal;
